import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpMethod, ReportType } from '@vision/ui/enums';
import {
  ApiResponse,
  CopyReportRequest,
  CreateNewReportGraphicRequest,
  CreateNewReportRequest,
  HttpBaseRequest,
  ReportDetail,
  ReportFavoriteDetail,
  DigestJobResult,
  ReportPermissions,
  ReportWidget,
  ShareDashboardByEmailRequest,
  ShareReportByEmailRequest,
  UpdateReportsPermissionsRequest,
  User,
  UserGroup,
} from '@vision/ui/interfaces';
import { buildEndpointWithQuery, createHttpExtraOptions, sortByNameAlphabetical } from '@vision/ui/utils';
import { axiosBaseQuery } from './base.service';
import { fromCachedReportResults } from './table-dispatch-report.mapper';

function normalizeCachedJobResultsResponse(response: unknown): DigestJobResult[] {
  if (Array.isArray(response)) {
    return fromCachedReportResults(response as DigestJobResult[]);
  }

  const apiResponse = response as Partial<ApiResponse<DigestJobResult[]>>;

  if (Array.isArray(apiResponse?.data)) {
    return fromCachedReportResults(apiResponse.data);
  }

  return [];
}

export const ReportsService = createApi({
  reducerPath: 'ReportsService',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['GetReports', 'GetFavoriteReports', 'GetReportGraphicDetail'],
  endpoints: (builder) => ({
    createNewReport: builder.mutation<ApiResponse<ReportDetail>, CreateNewReportRequest>({
      query: (data) => ({
        url: `/v2/accounts/${data.node_id}/dashboards`,
        method: HttpMethod.POST,
        data,
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleSuccess: false,
            translationMessageKey: 'http.message.reports.createNewReport.{{status}}',
          },
        }),
      }),
    }),
    createNewAtaDashboard: builder.mutation<ApiResponse<ReportDetail>, CreateNewReportRequest>({
      query: ({ node_id }) => ({
        url: `/v2/accounts/${node_id}/dashboards/create_ata_dashboard`,
        method: HttpMethod.POST,
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleSuccess: false,
            translationMessageKey: 'http.message.reports.createNewReport.{{status}}',
          },
        }),
      }),
    }),
    deleteReport: builder.mutation<ApiResponse<ReportDetail>, { nodeId: string; reportId: string }>({
      query: ({ nodeId, reportId }) => ({
        url: `/v2/accounts/${nodeId}/dashboards/${reportId}/delete`,
        method: HttpMethod.POST,
        extraOptions: createHttpExtraOptions({
          notification: {
            translationMessageKey: 'http.message.reports.deleteReport.{{status}}',
          },
        }),
      }),
      invalidatesTags: ['GetReports', 'GetFavoriteReports'],
    }),
    copyReport: builder.mutation<
      ApiResponse<ReportDetail>,
      { nodeId: string; reportId: string; data: CopyReportRequest }
    >({
      query: ({ nodeId, reportId, data }) => ({
        url: `/v2/accounts/${nodeId}/dashboards/${reportId}/copy`,
        method: HttpMethod.POST,
        data,
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleSuccess: false,
            translationMessageKey: 'http.message.reports.copyReport.{{status}}',
          },
        }),
      }),
      invalidatesTags: ['GetReports'],
    }),
    shareReportByEmail: builder.mutation<
      ApiResponse<ReportDetail>,
      { nodeId: string; reportId: string; data: ShareReportByEmailRequest }
    >({
      query: ({ nodeId, reportId, data }) => ({
        url: `/v2/accounts/${nodeId}/dashboards/${reportId}/email_sharing`,
        method: HttpMethod.POST,
        data,
      }),
    }),
    shareDashboardByEmail: builder.mutation<
      ApiResponse<Record<string, never>>,
      { accountId: string; dashboardId: string; data: ShareDashboardByEmailRequest }
    >({
      query: ({ accountId, dashboardId, data }) => ({
        url: `/v2/accounts/${accountId}/dashboards/${dashboardId}/email_sharing`,
        method: HttpMethod.POST,
        data,
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleError: false,
            autoHandleSuccess: false,
          },
        }),
      }),
    }),
    getReports: builder.query<ApiResponse<ReportDetail[]>, { nodeId: string; query?: string; type?: ReportType }>({
      query: ({ nodeId, query, type }) => {
        const queryParams = new URLSearchParams(query ?? '');

        if (type) {
          queryParams.set('type', type);
        }

        return {
          url: buildEndpointWithQuery(`/v2/accounts/${nodeId}/dashboards`, queryParams.toString()),
          method: HttpMethod.GET,
          keepUnusedDataFor: 0,
        };
      },
      providesTags: ['GetReports'],
    }),
    getReportDetail: builder.query<ApiResponse<ReportDetail>, { nodeId: string; reportId: string }>({
      query: ({ nodeId, reportId }) => ({
        url: `/v2/accounts/${nodeId}/dashboards/${reportId}`,
        method: HttpMethod.GET,
      }),
    }),
    getReportPermissions: builder.query<ApiResponse<ReportPermissions>, { nodeId: string; reportId: string }>({
      query: ({ nodeId, reportId }) => ({
        url: `/v2/accounts/${nodeId}/dashboards/${reportId}/permissions`,
        method: HttpMethod.GET,
      }),
    }),
    updateReportPermissions: builder.mutation<
      ApiResponse<ReportPermissions>,
      { nodeId: string; reportId: string; data: UpdateReportsPermissionsRequest }
    >({
      query: ({ nodeId, reportId, data }) => ({
        url: `/v2/accounts/${nodeId}/dashboards/${reportId}/permissions`,
        method: HttpMethod.POST,
        data,
        extraOptions: createHttpExtraOptions({
          notification: {
            translationMessageKey: 'http.message.reports.updateReportPermissions.{{status}}',
          },
        }),
      }),
    }),
    updateReportHierarchy: builder.mutation<
      ApiResponse<ReportDetail>,
      { nodeId: string; reportId: string; parentId: string | null }
    >({
      query: ({ nodeId, reportId, parentId }) => ({
        url: `/v2/accounts/${nodeId}/dashboards/${reportId}/update_hierarchy`,
        method: HttpMethod.POST,
        data: {
          parent_id: parentId,
        },
      }),
      invalidatesTags: ['GetReports', 'GetFavoriteReports'],
    }),
    getReportsUserGroups: builder.query<
      ApiResponse<UserGroup[]>,
      HttpBaseRequest<{ nodeId: string; includeSelf?: boolean }>
    >({
      query: ({ nodeId, includeSelf = false }) => ({
        url: `/v2/accounts/${nodeId}/dashboards/user_groups`,
        method: HttpMethod.GET,
        params: {
          include_self: includeSelf,
        },
        extraOptions: createHttpExtraOptions({
          notification: {
            autoHandleSuccess: false,
          },
        }),
      }),
      transformResponse: (response: ApiResponse<UserGroup[]>) => {
        return { ...response, data: sortByNameAlphabetical(response.data) };
      },
    }),
    getReportUsers: builder.query<
      ApiResponse<User[]>,
      HttpBaseRequest<{ role?: string; nodeId?: string; query?: string }>
    >({
      query: ({ nodeId, query }) => ({
        url: buildEndpointWithQuery(`/v2/accounts/${nodeId}/dashboards/users`, query),
        method: HttpMethod.GET,
      }),
      transformResponse: (response: ApiResponse<User[]>) => {
        return { ...response, data: sortByNameAlphabetical(response.data) };
      },
    }),
    toggleReportFavorite: builder.mutation<
      ApiResponse<ReportDetail>,
      { nodeId: string; reportId: string; isFavorite: boolean }
    >({
      query: ({ nodeId, reportId, isFavorite }) => ({
        url: `/v2/accounts/${nodeId}/dashboards/${reportId}/favorite`,
        method: HttpMethod.POST,
        extraOptions: createHttpExtraOptions({
          notification: {
            translationMessageKey: `http.message.reports.${isFavorite ? 'addReportFavorite' : 'removeReportFavorite'}.{{status}}`,
          },
        }),
      }),
      invalidatesTags: (result, error) => {
        if (!error) {
          return ['GetReports', 'GetFavoriteReports'];
        }
        return [];
      },
    }),
    getFavoriteReports: builder.query<ApiResponse<ReportFavoriteDetail[]>, { nodeId: string }>({
      query: ({ nodeId }) => ({
        url: `/v2/accounts/${nodeId}/dashboards/favorite`,
        method: HttpMethod.GET,
      }),
      providesTags: ['GetFavoriteReports'],
      transformResponse: (response: ApiResponse<ReportFavoriteDetail[]>) => {
        return {
          ...response,
          data: response.data.sort((a, b) => {
            return new Date(b.dashboard.updated_at).getTime() - new Date(a.dashboard.updated_at).getTime();
          }),
        };
      },
    }),
    getCachedJobResults: builder.query<DigestJobResult[], { accountId: string; jobResultsKey: string }>({
      query: ({ accountId, jobResultsKey }) => ({
        url: `/v2/accounts/${accountId}/dashboards/cached_job_results/${jobResultsKey}`,
        method: HttpMethod.GET,
      }),
      transformResponse: normalizeCachedJobResultsResponse,
    }),
    createNewReportGraphicWithWizard: builder.mutation<
      ApiResponse<ReportDetail>,
      { nodeId: string; reportId: string; data: CreateNewReportGraphicRequest }
    >({
      query: ({ nodeId, reportId, data }) => ({
        url: `/v2/accounts/${nodeId}/dashboards/${reportId}/dashboard_widgets`,
        method: HttpMethod.POST,
        data,
      }),
      invalidatesTags: ['GetFavoriteReports'],
    }),
    getReportGraphicDetail: builder.query<
      ApiResponse<ReportWidget>,
      { nodeId: string; reportId: string; widgetId: string; type: ReportType }
    >({
      query: ({ nodeId, reportId, widgetId, type }) => ({
        url: `/v2/accounts/${nodeId}/dashboards/${reportId}/dashboard_widgets/${widgetId}`,
        method: HttpMethod.GET,
        params: {
          type,
        },
      }),
      providesTags: ['GetReportGraphicDetail'],
    }),
    createNewReportGraphic: builder.mutation<
      ApiResponse<ReportDetail>,
      { nodeId: string; reportId: string; data: ReportWidget }
    >({
      query: ({ nodeId, reportId, data }) => ({
        url: `/v2/accounts/${nodeId}/dashboards/${reportId}/dashboard_widgets`,
        method: HttpMethod.POST,
        data,
        extraOptions: createHttpExtraOptions({
          notification: {
            translationMessageKey: 'http.message.reports.createChart.{{status}}',
          },
        }),
      }),
      invalidatesTags: ['GetFavoriteReports'],
    }),
    updateReportGraphic: builder.mutation<
      ApiResponse<ReportDetail>,
      { nodeId: string; reportId: string; data: ReportWidget; widgetId: string }
    >({
      query: ({ nodeId, reportId, data, widgetId }) => ({
        url: `/v2/accounts/${nodeId}/dashboards/${reportId}/dashboard_widgets/${widgetId}/update`,
        method: HttpMethod.POST,
        data,
        extraOptions: createHttpExtraOptions({
          notification: {
            translationMessageKey: 'http.message.reports.updateChart.{{status}}',
          },
        }),
      }),
      invalidatesTags: ['GetReportGraphicDetail', 'GetFavoriteReports'],
    }),
  }),
});

export const {
  useCreateNewReportMutation,
  useCreateNewAtaDashboardMutation,
  useDeleteReportMutation,
  useCopyReportMutation,
  useShareDashboardByEmailMutation,
  useShareReportByEmailMutation,
  useLazyGetReportsQuery,
  useGetReportDetailQuery,
  useLazyGetReportPermissionsQuery,
  useUpdateReportPermissionsMutation,
  useUpdateReportHierarchyMutation,
  useGetReportsUserGroupsQuery,
  useGetReportUsersQuery,
  useToggleReportFavoriteMutation,
  useGetFavoriteReportsQuery,
  useLazyGetCachedJobResultsQuery,
  useCreateNewReportGraphicWithWizardMutation,
  useGetReportGraphicDetailQuery,
  useUpdateReportGraphicMutation,
  useCreateNewReportGraphicMutation,
  useLazyGetReportUsersQuery,
} = ReportsService;
